import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getTimezone } from './datetime'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * Parse a time string in HH:mm format and return hours and minutes
 * @param timeStr - Time string in HH:mm format (e.g., "06:00" or "6:00")
 * @returns Object with hours and minutes, or null if invalid
 */
function parseTimeString(timeStr: string | undefined): { hours: number; minutes: number } | null {
  if (!timeStr) return null

  // Match HH:mm or H:mm format
  const match = timeStr.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null

  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return { hours, minutes }
}

/**
 * Get scheduling interval start time from environment variables
 * @returns Object with hours and minutes
 * @throws Error if SCHEDULING_START_HOUR is not set or invalid
 */
export function getSchedulingIntervalStart(): { hours: number; minutes: number } {
  const envValue =
    import.meta.env.VITE_SCHEDULING_START_HOUR ||
    (typeof window !== 'undefined' ? window.ENV?.SCHEDULING_START_HOUR : undefined)

  const parsed = parseTimeString(envValue)
  if (!parsed) {
    throw new Error('SCHEDULING_START_HOUR environment variable must be set and in HH:mm format (e.g., "07:00")')
  }
  return parsed
}

/**
 * Get scheduling interval end time from environment variables
 * @returns Object with hours and minutes
 * @throws Error if SCHEDULING_END_HOUR is not set or invalid
 */
export function getSchedulingIntervalEnd(): { hours: number; minutes: number } {
  const envValue =
    import.meta.env.VITE_SCHEDULING_END_HOUR ||
    (typeof window !== 'undefined' ? window.ENV?.SCHEDULING_END_HOUR : undefined)

  const parsed = parseTimeString(envValue)
  if (!parsed) {
    throw new Error('SCHEDULING_END_HOUR environment variable must be set and in HH:mm format (e.g., "06:00")')
  }
  return parsed
}

/**
 * Get the default scheduling interval bounds
 * Reads from SCHEDULING_START_HOUR and SCHEDULING_END_HOUR environment variables
 * Times are interpreted in the configured timezone (from TIME_ZONE env var)
 * @param date - Optional date to calculate bounds for. Defaults to today.
 * @returns Object with start and end times as ISO strings (in UTC)
 */
export function getDefaultSchedulingBounds(date?: Date) {
  const timezone = getTimezone()
  const startTime = getSchedulingIntervalStart()
  const endTime = getSchedulingIntervalEnd()

  // Get base date in configured timezone
  // If date is provided, convert it to the timezone; otherwise use current time in timezone
  const baseDate = date ? dayjs(date).tz(timezone) : dayjs().tz(timezone)

  // Create intervals in the configured timezone
  const startInterval = baseDate.startOf('day').hour(startTime.hours).minute(startTime.minutes).second(0).millisecond(0)
  const endInterval = baseDate
    .startOf('day')
    .add(1, 'day')
    .hour(endTime.hours)
    .minute(endTime.minutes)
    .second(0)
    .millisecond(0)

  // Convert to UTC for return values
  return {
    start: startInterval.utc().toISOString(),
    end: endInterval.utc().toISOString(),
    startFormatted: startInterval.utc().format('YYYY-MM-DDTHH:mm') + 'Z',
    endFormatted: endInterval.utc().format('YYYY-MM-DDTHH:mm') + 'Z',
  }
}

/**
 * Check if a time falls within the default scheduling bounds
 * @param time - ISO string or Date to check
 * @param date - Optional date to calculate bounds for. Defaults to today.
 * @returns true if the time is within bounds (based on SCHEDULING_START_HOUR and SCHEDULING_END_HOUR in configured timezone)
 */
export function isWithinDefaultSchedulingBounds(time: string | Date, date?: Date): boolean {
  const bounds = getDefaultSchedulingBounds(date)
  const timeToCheck = typeof time === 'string' ? dayjs(time).utc() : dayjs(time).utc()
  const start = dayjs(bounds.start).utc()
  const end = dayjs(bounds.end).utc()

  return (
    (timeToCheck.isAfter(start) || timeToCheck.isSame(start)) && (timeToCheck.isBefore(end) || timeToCheck.isSame(end))
  )
}

/**
 * Clamp a time to the default scheduling bounds
 * @param time - ISO string or Date to clamp
 * @param date - Optional date to calculate bounds for. Defaults to today.
 * @returns Clamped time as ISO string
 */
export function clampToDefaultSchedulingBounds(time: string | Date, date?: Date): string {
  const bounds = getDefaultSchedulingBounds(date)
  const timeToCheck = typeof time === 'string' ? dayjs(time).utc() : dayjs(time).utc()
  const start = dayjs(bounds.start).utc()
  const end = dayjs(bounds.end).utc()

  if (timeToCheck.isBefore(start)) {
    return bounds.start
  }
  if (timeToCheck.isAfter(end)) {
    return bounds.end
  }
  return timeToCheck.toISOString()
}
